import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../common/debounce";
import { Card } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useTranslation } from "react-i18next";
import CommonPagination from "../../components/widgets/common_pagination";
import { CommonTextField } from "../../components/widgets/common_textField";
import CommonButton from "../../components/widgets/common_button";
import JobsService from "../../service/jobs.service";
import Toaster from "../../components/common/toaster";
import { Eye, X, Users } from "lucide-react";

const JobReport = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  // Initialize state with potential restoration from localStorage
  const getInitialState = () => {
    const savedPaginationState = localStorage.getItem("usersPaginationState");
    if (savedPaginationState) {
      try {
        const paginationState = JSON.parse(savedPaginationState);
        const isRecent =
          Date.now() - paginationState.timestamp < 30 * 60 * 1000;
        if (isRecent) {
          localStorage.removeItem("usersPaginationState");
          return {
            page: paginationState.page || 1,
            size: paginationState.size || 10,
            search: paginationState.search || "",
            searchInput: paginationState.search || "",
          };
        } else {
          localStorage.removeItem("usersPaginationState");
        }
      } catch (error) {
        localStorage.removeItem("usersPaginationState");
      }
    }
    return {
      page: 1,
      size: 10,
      search: "",
      searchInput: "",
    };
  };

  const initialState = getInitialState();
  const [page, setPage] = useState(initialState.page);
  const [size, setSize] = useState(initialState.size);
  const [search, setSearch] = useState(initialState.search);
  const [jobReports, setJobReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [categoriesDialogOpen, setCategoriesDialogOpen] = useState(false);
  const [selectedUserCategories] = useState([]);
  const [userDataDialogOpen, setUserDataDialogOpen] = useState(false);
  const [selectedUserData] = useState(null);
  const isMountedRef = useRef(true);
  const isInitialLoadRef = useRef(true);

  const imagePlaceholder =
    "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM=";

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: size,
      };

      // Only add search parameter if it's not empty
      if (search && search.trim() !== "") {
        params.search = search.trim();
      }

      const response = await JobsService.getJobReport(params);

      if (response?.data?.data) {
        setJobReports(response.data.data);
        setTotalUsers(response.data.total || response.data.data.length);
        setTotalPages(
          Math.ceil((response.data.total || response.data.data.length) / size)
        );
      }
    } catch (error) {
      Toaster(
        "error",
        error?.response?.data?.message || "Failed to fetch users"
      );
      setJobReports([]);
    } finally {
      setLoading(false);
    }
  }, [page, size, search]);

  // Load users on component mount and when page/size changes
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, search]);

  // Debounced search function
  const debouncedSearch = useDebounce((searchTerm) => {
    setSearch(searchTerm);
    // Only reset page if we're not restoring state
    if (!isInitialLoadRef.current) {
      setPage(1); // Reset to first page when searching
    }
  }, 500);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleDataSize = (value) => {
    setSize(value);
    setPage(1);
  };

  // Format text: capitalize first character and remove underscores
  const formatText = (text) => {
    if (!text) return "-";
    return text
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
  };

  return (
    <div className="space-y-1">
      {/* Sticky Dashboard Metrics */}
      <div className="sticky top-0 z-10 pb-4 flex-shrink-0">
        {/* <div className="flex justify-start gap-6">
         
          <Card className="p-6 rounded-lg shadow-sm h-24 w-64">
            <div className="flex items-center gap-4 h-full">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Job Seekers</p>
                <p className="text-3xl font-bold text-blue-600">
                  {dashboardMetrics.totalJobSeekers}
                </p>
              </div>
            </div>
          </Card>

         
          <Card className="p-6 rounded-lg shadow-sm h-24 w-64">
            <div className="flex items-center gap-4 h-full">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-1">Total Employees</p>
                <p className="text-3xl font-bold text-purple-600">
                  {dashboardMetrics.totalEmployees}
                </p>
              </div>
            </div>
          </Card>
        </div> */}
      </div>

      {/* User List Section */}
      <Card className="p-10 grid gap-6">
        <div className="flex items-center justify-between gap-4 max-lg:flex-col">
          {/* Left side - User List heading */}
          <h3 className="text-xl tab:text-2xl font-bold">
            {t("jobreport.jobreportList")}
          </h3>
        </div>

        <div className="overflow-y-auto max-h-[410px]">
          <Table className="whitespace-nowrap">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[8%]">No</TableHead>
                <TableHead className="w-[15%]">First Name</TableHead>
                <TableHead className="w-[20%]">Email</TableHead>
                <TableHead className="w-[15%]">Job Title</TableHead>
                <TableHead className="w-[12%]">Job Image</TableHead>
                <TableHead className="w-[12%]">Created Date</TableHead>
                <TableHead className="w-[18%]">Reported Message</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="h-40">
                  <TableCell
                    colSpan={8}
                    className="text-center text-black/50 font-medium text-xl"
                  >
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : jobReports.length > 0 ? (
                jobReports.map((item, index) => (
                  <TableRow key={item._id || index}>
                    <TableCell className="ps-4">
                      {(page - 1) * size + index + 1}
                    </TableCell>
                    <TableCell>{item.userId.firstName || "-"}</TableCell>
                    <TableCell>{item.userId.email || "-"}</TableCell>
                    <TableCell>{item.jobId.name || "-"}</TableCell>
                    <TableCell>
                      <div className="flex">
                        <div className="h-8 w-8 overflow-hidden rounded-lg">
                          <img
                            src={item.jobId.jobImg || imagePlaceholder}
                            alt="Job"
                            className="h-full w-full object-cover"
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(item.jobId.createdAt).toLocaleDateString() ||
                        "-"}
                    </TableCell>
                    <TableCell>{item.report || "-"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow className="h-40">
                  <TableCell
                    colSpan={8}
                    className="text-center text-black/50 font-medium text-xl"
                  >
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between max-md:flex-col gap-4">
          {totalUsers > size && (
            <CommonPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={(newPage) => setPage(newPage)}
              pageSize={size}
              onPageSizeChange={handleDataSize}
              className=""
            />
          )}
        </div>

   

       

      
      </Card>
    </div>
  );
};

export default JobReport;
